import { Hash, Tag } from 'lucide-react';
import { HighlightSnippet, getTranslationHighlight } from './HighlightSnippet';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/ui/design-system/components/ui/tooltip';
import { Badge } from '@/ui/design-system/components/ui/badge';
import { CommandItem } from '@/ui/design-system/components/ui/command';

function isModifiedEvent(event) {
    return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

function SearchResult({ hit, onSelect, onModifierClick }) {
    const { objectID, ...rest } = hit;
    const translation = getTranslationHighlight(hit._highlightResult);
    const matchedProducts =
        hit.products?.filter(
            (p) => p._highlightResult?.name?.matchLevel !== 'none',
        ) || [];
    const matchedHashtagIndices = (hit._highlightResult?.hashtags || [])
        .map((h, i) => (h.matchLevel !== 'none' ? i : null))
        .filter((i) => i !== null);

    return (
        <CommandItem
            key={objectID}
            value={`${rest.name}-${objectID}`}
            className="min-h-[44px] !py-0"
            onSelect={onSelect}
            onClickCapture={(e) => {
                if (isModifiedEvent(e) && onModifierClick) {
                    e.stopPropagation();
                    onModifierClick();
                }
            }}
        >
            <span className="min-w-0 flex-1">
                {rest.parentName && (
                    <span className="block truncate text-[10px] capitalize leading-tight text-muted-foreground">
                        {rest.parentName}
                    </span>
                )}
                <span className="block truncate capitalize">{rest.name}</span>
                {translation && (
                    <span className="block truncate text-[10px] leading-tight text-muted-foreground">
                        <span className="font-semibold uppercase">
                            {translation[0].replace('name_', '')}:
                        </span>{' '}
                        <span className="capitalize italic">
                            <HighlightSnippet
                                hit={hit}
                                attribute={translation[0]}
                            />
                        </span>
                    </span>
                )}
            </span>
            {(matchedProducts.length > 0 ||
                matchedHashtagIndices.length > 0) && (
                <span className="ml-auto flex shrink-0 items-center gap-1">
                    {matchedProducts.length > 0 && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Badge
                                    variant="secondary"
                                    className="cursor-default"
                                >
                                    <Tag className="size-2.5" />
                                    {matchedProducts.length}
                                </Badge>
                            </TooltipTrigger>
                            <TooltipContent style={{ maxWidth: 288 }}>
                                <div
                                    style={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {matchedProducts.map((product, i) => (
                                        <span
                                            key={product.id || i}
                                            className="mr-1"
                                        >
                                            <HighlightSnippet
                                                hit={product}
                                                attribute="name"
                                            />
                                        </span>
                                    ))}
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    )}
                    {matchedHashtagIndices.length > 0 && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Badge
                                    variant="secondary"
                                    className="cursor-default"
                                >
                                    <Hash className="size-2.5" />
                                    {matchedHashtagIndices.length}
                                </Badge>
                            </TooltipTrigger>
                            <TooltipContent style={{ maxWidth: 288 }}>
                                <div
                                    style={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {matchedHashtagIndices.map((hashtagIdx) => (
                                        <span key={hashtagIdx} className="mr-1">
                                            <HighlightSnippet
                                                hit={hit}
                                                attribute={`hashtags[${hashtagIdx}]`}
                                            />
                                        </span>
                                    ))}
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    )}
                </span>
            )}
        </CommandItem>
    );
}

export default SearchResult;
